import React, {memo, useCallback, useEffect, useMemo, useState} from 'react';
import {PasswordContainer} from '../../../../components/PasswordContainer';
import {SettingsPageContainer} from '../../../../components/Settings/SettingsPageContainer';
import {useHistory, useLocation, useParams} from 'react-router-dom';
import {useMessage} from '../../../../hooks/message';
import {useQuestion} from '../../../../hooks/question';
import {useRPC} from '../../../../hooks/rpc';
import {VirtualControlModel} from '../../../../store/control/state';
import {SelectItem} from '../../../../components/Select';
import {BridgeDeviceModel} from '../../../../types/bridge-device';
import {
    BRIDGE_DEVICE_TYPE_ID_STS_PLUS,
    CONTROL_TYPE_ID_THERMOSTAT,
    CONTROL_TYPE_ID_GLASS_DEVICE_BUTTON, ControlModel, CONTROL_TYPE_ID_GLASS_DEVICE_SCREEN, BRIDGE_DEVICE_TYPE_ID_EIO
} from "../../../../types/control"
import {useMapSelector} from '../../../../hooks/map-selector';
import {useMap} from '../../../../hooks/map';
import {useFilterSelector} from '../../../../hooks/filter-selector';
import {SettingReadOnlyRowComponent} from '../../../../components/Settings/SettingReadOnlyRowComponent';
import {SettingSelectComponent} from '../../../../components/Settings/SettingSelectComponent';
import {useSetSelector} from '../../../../hooks/set-selector';
import {useGlassControl} from '../../../../hooks/glass-control';
import {SettingCheckBoxComponent} from '../../../../components/Settings/SettingCheckboxComponent';
import {useLocalization} from "../../../../hooks/localization";
import { useSelector } from 'react-redux';

const BACK_URL = '/settings/virtual-control';

export const VirtualControlEditScreen = memo(function VirtualControlEditScreen() {
    const {t} = useLocalization();
    const params = useParams<{ id: string }>();
    const {success, failure} = useMessage();
    const question = useQuestion();
    const [rpc] = useRPC();
    const history = useHistory();
    const location = useLocation();

    const controlSet = useSetSelector(state => state.control.controlList);
    const controlTypeSet = useSetSelector(state => state.control.controlTypeList);
    const virtualControlSet = useSetSelector(state => state.control.virtualControlList);
    const bridgeDeviceOptionList: SelectItem<BridgeDeviceModel>[] = useMapSelector(
        state => state.control.bridgeDeviceList.filter(device =>  device?.type_id !== BRIDGE_DEVICE_TYPE_ID_EIO),
        bridgeDevice => ({
            value: bridgeDevice,
            label: bridgeDevice.name,
        }));

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const selected = useMemo(() => location.state || virtualControlSet[params?.id] || null, []);
    const slot = !location.state ? ((selected?.slot?.toString()) || null) : null;
    const [bridgeDevice, setBridgeDevice] = useState<SelectItem<BridgeDeviceModel>>(
        (selected == null ? null :
            bridgeDeviceOptionList.find(option => option.value.id === selected.bridge_device_id) || null),
    );

    const gatewayList = useSelector(state => state.control.gatewayList)
    const deviceChecking = useMemo(() => {
        for (let i = 1; i < gatewayList.length; i++) {
            if (
                gatewayList[i].settings.rf.channel !== gatewayList[0].settings.rf.channel ||
                gatewayList[i].settings.rf.network_id !== gatewayList[0].settings.rf.network_id
            ) {
               return false
            }
        }
        return true
    }, [gatewayList])


    const controlBridgeDeviceOptionList: SelectItem<BridgeDeviceModel>[] = useMap(useFilterSelector(
        state => state.control.bridgeDeviceList,
        bridgeDeviceItem => (!deviceChecking ? bridgeDeviceItem.gateway_id === bridgeDevice?.value?.gateway_id : true) && bridgeDeviceItem.id !== bridgeDevice?.value?.id,
        [bridgeDevice?.value?.id],
    ), bridgeDevice => ({value: bridgeDevice, label: bridgeDevice.name}));
    const [controlBridgeDevice, setControlBridgeDevice] = useState<SelectItem<BridgeDeviceModel>>(
        (selected == null ? null :
            controlBridgeDeviceOptionList.find(option => option.value.id === controlSet[selected.control_id]?.bridge_device_id)
            || null),
    );

    const controlOptionList = useMap(useFilterSelector(
        state => state.control.controlList,
        control => control.type_id !== CONTROL_TYPE_ID_THERMOSTAT && control.type_id !== CONTROL_TYPE_ID_GLASS_DEVICE_BUTTON && control.type_id !== CONTROL_TYPE_ID_GLASS_DEVICE_SCREEN &&
            controlBridgeDevice?.value?.id === control.bridge_device_id &&
            controlTypeSet[control.type_id].allowed_bridge_device_type_id_list.indexOf(bridgeDevice?.value?.type_id) !== -1,
        [selected?.bridge_device_id, controlBridgeDevice?.value?.id, controlTypeSet],
    ), control => ({
        value: control,
        label: control.name,
    }));
    const [control, setControl] = useState<SelectItem<ControlModel>>(
        (selected == null ? null :
            controlOptionList.find(option => option.value.id === selected.control_id) || null),
    );
    const [navigateToSTPEditor, setNavigateToSTPEditor] = useState<boolean>();

    const bridgeDeviceTypeSet = useSetSelector(state => state.control.bridgeDeviceTypeList);
    const bridgeDeviceType = useMemo(() => bridgeDevice == null ? null : bridgeDeviceTypeSet[bridgeDevice.value.type_id],
        [bridgeDevice, bridgeDeviceTypeSet]);

    const {buttonNumber, buttonNumberOptionList, setButtonNumber} =
        useGlassControl(selected, bridgeDevice?.value?.id || null, bridgeDeviceType, control?.value?.type_id);

    const setNavigateStpEditorCheckbox = useCallback(() => {
        setNavigateToSTPEditor(!navigateToSTPEditor);
    }, [navigateToSTPEditor]);

    const onBackPressed = useCallback(() => {
        history.push(BACK_URL);
    }, [history]);

    const goToStpEditorWithState = useCallback((state: unknown) => {
        history.push({pathname: `/settings/stp-editor/${bridgeDevice.value.id}/content/`, state});
    }, [bridgeDevice, history]);

    const onDeletePressed = useCallback(() => {
        question(t('are_you_sure_to_delete_virtual_control'), () => {
            return rpc.deleteVirtualControl(selected.id)
                .then(() => {
                    success(t('virtual_control_successfully_deleted'));
                    onBackPressed();
                })
                .catch(failure);
        });
    }, [t, success, failure, onBackPressed, question, rpc, selected]);

    const isFieldsValid = useCallback((): boolean => {
        if (bridgeDevice == null) {
            failure(t('pls_select_a_valid_device'));

            return false;
        }

        if (control == null) {
            failure(t('pls_select_a_valid_control'));

            return false;
        }

        if (bridgeDeviceType?.button_count > 0) {
            if (buttonNumber?.value == null) {
                failure(t('pls_select_button_number'));

                return false;
            }
        }

        return true;
    }, [t, bridgeDevice, control, failure, bridgeDeviceType, buttonNumber]);

    const onCreatePressed = useCallback(() => {
        if (!isFieldsValid()) {
            return;
        }

        const virtualControl: VirtualControlModel = {
            id: selected?.id || null,
            slot: location.state ? -1 : (selected ? selected?.slot : -1),
            bridge_device_id: bridgeDevice.value.id,
            control_id: control.value.id,
            parameters: bridgeDeviceType.is_io_device ? null : {
                button_number: buttonNumber?.value,
            },
        };

        return rpc.updateVirtualControl(virtualControl)
            .then(id => {
                if (navigateToSTPEditor) {
                    goToStpEditorWithState({
                        controlId: id,
                        bridgeDeviceId: bridgeDevice.value.id,
                    });
                } else {
                    success(t(selected != null ? 'virtual_control_successfully_edited' : 'virtual_control_successfully_saved'));
                    onBackPressed();
                }
            }).catch(failure);
    }, [
        t,
        isFieldsValid,
        location.state,
        selected,
        rpc,
        bridgeDevice,
        control,
        success,
        failure,
        onBackPressed,
        buttonNumber,
        bridgeDeviceType?.is_io_device,
        navigateToSTPEditor,
        goToStpEditorWithState,
    ]);

    const buttons = useMemo(() => [
        {
            text: t('delete_virtual_control'),
            className: 'mulberry-wood',
            onClick: onDeletePressed,
            hidden: !(selected) || location.state,
        },
        {
            text: t('save'),
            className: 'green',
            onClick: onCreatePressed,
        },
        {
            text: t('cancel'),
            className: 'red',
            url: BACK_URL,
        },
    ], [t, onCreatePressed, onDeletePressed, selected, location.state]);

    useEffect(() => {
        setControlBridgeDevice(controlBridgeDeviceOptionList.find(option => option.value.id === controlBridgeDevice?.value?.id) || null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [controlBridgeDeviceOptionList]);

    useEffect(() => {
        setControl(controlOptionList.find(option => option.value.id === control?.value?.id) || null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [controlOptionList]);

    //todo modifiye sts button for sts scenario
    const stpUIEntityList = useSelector((state) => state.control.stpUIList);
    const deviceControlStpUI = stpUIEntityList.filter(control => control.bridge_device_id === bridgeDevice?.value.id);
    const rowValuesStpUI = deviceControlStpUI.map(control => control.row_number);
    const defaultRowValues = buttonNumberOptionList.map((option) => option.value);
    const newrowValuesStpUI = rowValuesStpUI.map(num => num - 1);
    const matchingRowValues = defaultRowValues.filter((buttonNumber) => !newrowValuesStpUI.includes(buttonNumber));
    const remainingOptionsWithLabel = matchingRowValues.map(option => ({value: option , label: `${option+1}`}));

    return (
        <PasswordContainer requiredRole='service' isFullscreen={true}>
            <SettingsPageContainer
                buttons={buttons}
                className='service-edit'
                backUrl={BACK_URL}>
                <div className='content-divider'>
                    <div className='content-wrapper'>
                        {
                            renderIf(slot)(() =>
                                <>
                                    <SettingReadOnlyRowComponent
                                        title='Slot'
                                        value={slot}/>
                                </>,
                            )
                        }
                        <SettingSelectComponent
                            label={t('device')}
                            options={bridgeDeviceOptionList}
                            onChange={setBridgeDevice}
                            value={bridgeDevice}/>
                        <SettingSelectComponent
                            label={t('device_to_control')}
                            options={controlBridgeDeviceOptionList}
                            onChange={setControlBridgeDevice}
                            value={controlBridgeDevice}/>
                        <SettingSelectComponent
                            label={t('control')}
                            options={controlOptionList}
                            onChange={setControl}
                            value={control}/>
                    </div>
                    <div className='content-wrapper'>
                        {
                            renderIf(bridgeDeviceType?.button_count > 0)(() =>
                                <SettingSelectComponent
                                    label={t('button_number')}
                                    options={remainingOptionsWithLabel}
                                    onChange={setButtonNumber}
                                    value={buttonNumber}/>,
                            )
                        }
                        {
                            renderIf(selected?.id == null &&
                                bridgeDeviceType?.page_count > 0)(() =>
                                <SettingCheckBoxComponent
                                    label={t(bridgeDeviceType?.id === BRIDGE_DEVICE_TYPE_ID_STS_PLUS ? 'go_to_sts_plus_editor' : 'go_to_stp_editor')}
                                    set={setNavigateStpEditorCheckbox}
                                    value={navigateToSTPEditor}/>,
                            )
                        }
                    </div>
                </div>
            </SettingsPageContainer>
        </PasswordContainer>
    );
});
